import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Crosshair, Cloud, Trophy, Moon, CloudRain, Waves, TreePine, Flame, Play, Pause, Square, Volume2, Loader2, Headphones, Music, Mic, Timer, X, Upload, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  crosshair: Crosshair, cloud: Cloud, trophy: Trophy, moon: Moon,
  "cloud-rain": CloudRain, waves: Waves, trees: TreePine, flame: Flame,
};

// Soundscape card with play/stop for looping ambient audio
function SoundscapeCard({ item }) {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(null); // timer in minutes
  const timerRef = useRef(null);
  const Icon = ICONS[item.icon] || Volume2;

  const togglePlay = () => {
    if (playing) {
      setPlaying(false);
      if (timerRef.current) clearTimeout(timerRef.current);
    } else {
      setPlaying(true);
      if (duration) {
        timerRef.current = setTimeout(() => setPlaying(false), duration * 60 * 1000);
      }
    }
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <Card data-testid={`soundscape-${item.id}`} className={`border-0 shadow-md overflow-hidden transition-all hover:shadow-lg ${playing ? "ring-2 ring-white/50" : ""}`}>
      <div className={`bg-gradient-to-br ${item.color} p-4 text-white`}>
        <div className="flex items-start justify-between mb-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <Icon className="w-5 h-5" />
          </div>
          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{item.category === "binaural" ? "Binaural" : "Soundscape"}</span>
        </div>
        <h3 className="font-bold text-base mb-1">{item.title}</h3>
        <p className="text-white/80 text-xs leading-relaxed mb-3">{item.description}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1.5">
            {[5, 10, 15, 30].map((m) => (
              <button
                key={m}
                onClick={() => setDuration(duration === m ? null : m)}
                className={`text-[10px] px-1.5 py-0.5 rounded ${duration === m ? "bg-white/40 font-bold" : "bg-white/15"}`}
              >
                {m}m
              </button>
            ))}
          </div>
          <Button
            data-testid={`play-soundscape-${item.id}`}
            size="sm"
            onClick={togglePlay}
            className={`h-8 w-8 rounded-full p-0 ${playing ? "bg-white text-gray-800" : "bg-white/20 text-white hover:bg-white/30"}`}
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </Button>
        </div>
      </div>
      {playing && (
        <div className="bg-black/80 px-4 py-2 flex items-center space-x-2">
          <div className="flex space-x-0.5">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="w-1 bg-white/60 rounded-full animate-pulse" style={{ height: `${8 + Math.random() * 12}px`, animationDelay: `${i * 0.15}s` }} />
            ))}
          </div>
          <span className="text-white/70 text-xs flex-1">Playing{duration ? ` · ${duration}m` : " · Loop"}</span>
          <button onClick={() => { setPlaying(false); if (timerRef.current) clearTimeout(timerRef.current); }} className="text-white/50 hover:text-white">
            <Square className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </Card>
  );
}

// Guided session card
function GuidedSessionTypeCard({ session, onGenerate, generating }) {
  return (
    <Card data-testid={`guided-type-${session.id}`} className="border-0 shadow-md bg-white/90 hover:shadow-lg transition-all">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 text-base mb-0.5">{session.title}</h3>
            <p className="text-gray-500 text-xs mb-2">{session.description}</p>
            <div className="flex items-center space-x-2 text-xs text-gray-400">
              <Mic className="w-3 h-3" />
              <span>Voice: {session.voice}</span>
              <span>·</span>
              <span>AI Generated</span>
            </div>
          </div>
          <Button
            data-testid={`generate-${session.id}`}
            size="sm"
            onClick={() => onGenerate(session.id)}
            disabled={generating === session.id}
            className="bg-indigo-600 hover:bg-indigo-700 h-9 px-3"
          >
            {generating === session.id ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Generating...</>
            ) : (
              <><Headphones className="w-3.5 h-3.5 mr-1" /> Generate</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Audio player for generated sessions (uses OpenAI TTS with Web Speech fallback)
function AudioPlayer({ session, onClose }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  const togglePlay = () => {
    if (playing) {
      if (audioRef.current) audioRef.current.pause();
      setPlaying(false);
    } else {
      if (audioRef.current && audioRef.current.currentTime > 0) {
        audioRef.current.play().catch(() => {});
        setPlaying(true);
      } else {
        startSpeech();
      }
    }
  };

  const startSpeech = async () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }

    const paragraphs = session.script.split("\n\n").filter(Boolean);
    const fullText = paragraphs.join(". ... ");

    // Try OpenAI TTS first
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      // Split into chunks if too long (4096 char limit)
      const chunks = [];
      let current = "";
      for (const para of paragraphs) {
        if ((current + " " + para).length > 3800) {
          if (current) chunks.push(current);
          current = para;
        } else {
          current = current ? current + " ... " + para : para;
        }
      }
      if (current) chunks.push(current);

      // Generate audio for each chunk
      const urls = [];
      for (const chunk of chunks) {
        const res = await fetch(`${API}/audio/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ text: chunk, voice: session.voice || "coral" }),
        });
        if (!res.ok) throw new Error("TTS failed");
        const data = await res.json();
        urls.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      }

      setLoading(false);
      setPlaying(true);
      setProgress(0);

      // Play chunks sequentially
      let chunkIdx = 0;
      const totalChunks = urls.length;

      const playNext = () => {
        if (chunkIdx >= totalChunks) {
          setPlaying(false);
          setProgress(100);
          audioRef.current = null;
          return;
        }
        const audio = new Audio(urls[chunkIdx]);
        audioRef.current = audio;

        audio.ontimeupdate = () => {
          if (audio.duration) {
            const chunkProgress = audio.currentTime / audio.duration;
            const overallProgress = ((chunkIdx + chunkProgress) / totalChunks) * 100;
            setProgress(overallProgress);
          }
        };
        audio.onended = () => {
          chunkIdx++;
          playNext();
        };
        audio.onerror = () => {
          setPlaying(false);
          setProgress(0);
          audioRef.current = null;
        };
        audio.play().catch(() => {
          setPlaying(false);
          audioRef.current = null;
        });
      };
      playNext();

    } catch (err) {
      setLoading(false);
      // Fallback to Web Speech API
      fallbackWebSpeech(paragraphs);
    }
  };

  const fallbackWebSpeech = (paragraphs) => {
    const totalChars = paragraphs.join("").length || 1;
    let charOffset = 0;
    setPlaying(true);
    setProgress(0);

    const speakNext = (idx) => {
      if (idx >= paragraphs.length) {
        setPlaying(false);
        setProgress(100);
        return;
      }
      const utter = new window.SpeechSynthesisUtterance(paragraphs[idx]);
      utter.rate = 0.85;
      utter.pitch = 1.0;
      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find(v => v.name.includes("Samantha") || v.name.includes("Google") || v.name.includes("Daniel"));
      if (preferred) utter.voice = preferred;
      utter.onboundary = (e) => setProgress(((charOffset + e.charIndex) / totalChars) * 100);
      utter.onend = () => { charOffset += paragraphs[idx].length + 2; speakNext(idx + 1); };
      window.speechSynthesis.speak(utter);
    };
    speakNext(0);
  };

  const stop = () => {
    window.speechSynthesis.cancel();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    setPlaying(false);
    setProgress(0);
    setLoading(false);
  };

  useEffect(() => () => {
    window.speechSynthesis.cancel();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const est = session.duration_estimate_seconds || 120;
  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  return (
    <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-xl p-4 text-white shadow-lg">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="font-bold text-sm">{session.title}</h4>
          <p className="text-white/60 text-xs">{session.sport || "General"} · ~{fmt(est)}</p>
        </div>
        <button onClick={() => { stop(); onClose(); }} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex items-center space-x-3">
        <Button data-testid="audio-play-btn" onClick={togglePlay} disabled={loading} size="sm" className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 p-0">
          {loading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : playing ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
        </Button>
        <div className="flex-1">
          <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white/70 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
        {(playing || loading) && (
          <button onClick={stop} className="text-white/50 hover:text-white"><Square className="w-4 h-4" /></button>
        )}
      </div>
    </div>
  );
}

// Uploaded Audio Player
function UploadedAudioPlayer({ session, onClose }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = new Audio(session.file_url);
    audioRef.current = audio;
    audio.onloadedmetadata = () => setDuration(audio.duration);
    audio.ontimeupdate = () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration) setProgress((audio.currentTime / audio.duration) * 100);
    };
    audio.onended = () => { setPlaying(false); setProgress(100); };
    return () => { audio.pause(); audio.src = ""; };
  }, [session.file_url]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play().catch(() => {}); }
    setPlaying(!playing);
  };
  const stop = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setPlaying(false);
    setProgress(0);
    setCurrentTime(0);
  };
  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  return (
    <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-xl p-4 text-white shadow-lg">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="font-bold text-sm">{session.title}</h4>
          <p className="text-white/60 text-xs">{session.sport || "General"}{session.duration_minutes ? ` · ~${session.duration_minutes} min` : ""}</p>
        </div>
        <button onClick={() => { stop(); onClose(); }} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex items-center space-x-3">
        <Button data-testid="uploaded-audio-play-btn" onClick={toggle} size="sm" className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 p-0">
          {playing ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
        </Button>
        <div className="flex-1">
          <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white/70 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-white/50">
            <span>{fmt(currentTime)}</span>
            <span>{fmt(duration)}</span>
          </div>
        </div>
        {playing && <button onClick={stop} className="text-white/50 hover:text-white"><Square className="w-4 h-4" /></button>}
      </div>
    </div>
  );
}

// Main Audio Library Component
export default function AudioLibrary({ userRole }) {
  const [soundscapes, setSoundscapes] = useState([]);
  const [guidedTypes, setGuidedTypes] = useState([]);
  const [mySessions, setMySessions] = useState([]);
  const [uploadedSessions, setUploadedSessions] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [activePlayer, setActivePlayer] = useState(null);
  const [uploadedPlayer, setUploadedPlayer] = useState(null);
  const [subTab, setSubTab] = useState("guided");
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);

  const isAdmin = userRole && ["system-admin", "school-admin", "sport-performance-consultant", "coach", "team-admin"].includes(userRole);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [scRes, gtRes, msRes, upRes] = await Promise.all([
        axios.get(`${API}/audio/soundscapes`),
        axios.get(`${API}/audio/guided-sessions`),
        axios.get(`${API}/audio/guided-sessions/mine`, { headers }),
        axios.get(`${API}/audio/uploaded-sessions`, { headers }),
      ]);
      setSoundscapes(scRes.data);
      setGuidedTypes(gtRes.data);
      setMySessions(msRes.data);
      setUploadedSessions(upRes.data);
    } catch (e) {
      console.error("Failed to load audio data", e);
    }
  };

  const generateSession = async (sessionType) => {
    setGenerating(sessionType);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/audio/guided-sessions/generate`,
        { session_type: sessionType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Audio session generated!");
      setMySessions((prev) => [res.data, ...prev.filter(s => s.id !== res.data.id)]);
      setActivePlayer(res.data);
    } catch (e) {
      toast.error("Failed to generate audio: " + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const file = formData.get("file");
    if (!file || !file.size) { toast.error("Please select an audio file"); return; }
    if (!formData.get("title")?.trim()) { toast.error("Please enter a title"); return; }

    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/audio/uploaded-sessions`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success("Audio uploaded successfully!");
      setUploadedSessions((prev) => [res.data, ...prev]);
      setShowUploadForm(false);
      form.reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteUploaded = async (sessionId) => {
    if (!window.confirm("Delete this audio recording?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/audio/uploaded-sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUploadedSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (uploadedPlayer?.id === sessionId) setUploadedPlayer(null);
      toast.success("Audio deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const binaural = soundscapes.filter((s) => s.category === "binaural");
  const ambientSounds = soundscapes.filter((s) => s.category === "soundscape");

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex space-x-2 mb-5 overflow-x-auto">
        {[
          { id: "guided", label: "Guided Sessions", icon: Headphones },
          { id: "binaural", label: "Binaural Beats", icon: Music },
          { id: "soundscapes", label: "Soundscapes", icon: Waves },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`audio-subtab-${t.id}`}
            onClick={() => setSubTab(t.id)}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              subTab === t.id
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-white/80 text-gray-600 hover:bg-white"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" />
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Active player */}
      {activePlayer && (
        <div className="mb-5">
          <AudioPlayer session={activePlayer} onClose={() => setActivePlayer(null)} />
        </div>
      )}
      {uploadedPlayer && (
        <div className="mb-5">
          <UploadedAudioPlayer session={uploadedPlayer} onClose={() => setUploadedPlayer(null)} />
        </div>
      )}

      {/* Guided Sessions */}
      {subTab === "guided" && (
        <div>
          {/* Uploaded Audio Recordings */}
          {uploadedSessions.length > 0 && (
            <div className="mb-6">
              <h3 className="font-bold text-gray-900 dark:text-white text-base mb-1">Guided Audio</h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">Professional guided exercises recorded by your sport psychologist</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {uploadedSessions.map((session) => (
                  <Card
                    key={session.id}
                    data-testid={`uploaded-audio-${session.id}`}
                    className="bg-white/90 dark:bg-gray-800/90 border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                    onClick={() => setUploadedPlayer(session)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/40 dark:to-teal-900/40 flex items-center justify-center flex-shrink-0">
                          <Mic className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm text-gray-900 dark:text-white truncate">{session.title}</h4>
                          {session.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{session.description}</p>}
                          <div className="flex items-center gap-2 mt-2">
                            {session.sport && <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full font-medium">{session.sport}</span>}
                            {session.duration_minutes > 0 && <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Timer className="w-2.5 h-2.5" />{session.duration_minutes} min</span>}
                          </div>
                        </div>
                        <button className="p-2 rounded-full bg-emerald-600 text-white opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); setUploadedPlayer(session); }}>
                          <Play className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Upload form for admin */}
          {isAdmin && (
            <div className="mb-6">
              {!showUploadForm ? (
                <Button data-testid="upload-audio-btn" onClick={() => setShowUploadForm(true)} variant="outline" size="sm" className="text-xs">
                  <Upload className="w-3.5 h-3.5 mr-1.5" />Upload Audio
                </Button>
              ) : (
                <form onSubmit={handleUpload} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
                      <input name="title" required placeholder="e.g., Swimming Visualization" className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Sport</label>
                      <input name="sport" placeholder="e.g., Swimming" className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-white" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <textarea name="description" rows={2} placeholder="Brief description..." className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-white" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                      <select name="category" className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-white">
                        <option value="guided-exercise">Guided Exercise</option>
                        <option value="visualization">Visualization</option>
                        <option value="breathing">Breathing</option>
                        <option value="meditation">Meditation</option>
                        <option value="pre-game">Pre-Game Routine</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Duration (minutes)</label>
                      <input name="duration_minutes" type="number" min="1" max="30" placeholder="10" className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-white" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Audio File * (MP3, M4A, WAV — max 50MB)</label>
                    <input name="file" type="file" accept="audio/*" required className="w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 dark:text-gray-300" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowUploadForm(false)}>Cancel</Button>
                    <Button type="submit" size="sm" disabled={uploading} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      {uploading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Uploading...</> : <><Upload className="w-3.5 h-3.5 mr-1.5" />Upload</>}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}

          <h3 className="font-bold text-gray-900 dark:text-white text-base mb-3">Generate AI Guided Audio</h3>
          <p className="text-gray-500 text-sm mb-4">AI-generated personalized sessions using your sport & position. Each session is unique to you.</p>
          <div className="space-y-3 mb-6">
            {guidedTypes.map((gt) => (
              <GuidedSessionTypeCard
                key={gt.id}
                session={gt}
                onGenerate={generateSession}
                generating={generating}
              />
            ))}
          </div>

          {mySessions.length > 0 && (
            <>
              <h3 className="font-bold text-gray-900 text-base mb-3">Your Generated Sessions</h3>
              <div className="space-y-2">
                {mySessions.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`my-session-${s.id}`}
                    className="flex items-center justify-between bg-white/90 rounded-lg p-3 shadow-sm hover:shadow-md transition-all cursor-pointer"
                    onClick={() => setActivePlayer(s)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <Headphones className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-gray-900">{s.title}</p>
                        <p className="text-xs text-gray-400">{s.sport} · {s.voice} · {new Date(s.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <Play className="w-4 h-4 text-gray-400" />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Binaural Beats */}
      {subTab === "binaural" && (
        <div>
          <h3 className="font-bold text-gray-900 text-base mb-1">Binaural Beats</h3>
          <p className="text-gray-500 text-sm mb-4">Use headphones for best effect. Binaural beats work by playing slightly different frequencies in each ear.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {binaural.map((s) => <SoundscapeCard key={s.id} item={s} />)}
          </div>
        </div>
      )}

      {/* Soundscapes */}
      {subTab === "soundscapes" && (
        <div>
          <h3 className="font-bold text-gray-900 text-base mb-1">Ambient Soundscapes</h3>
          <p className="text-gray-500 text-sm mb-4">Nature sounds for focus, relaxation, and mental recovery.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {ambientSounds.map((s) => <SoundscapeCard key={s.id} item={s} />)}
          </div>
        </div>
      )}
    </div>
  );
}
